import { ID } from '../types/common';
import { Folder } from '../models/folder';
import {
  IFolderRepository,
  IFolderHierarchyBuilder,
  ISpecialFolderFactory,
} from '../repositories/folderRepository';
import { IPromptRepository } from '../repositories/promptRepository';
import { DomainEventPublisher, DomainEventFactory } from '../events/domainEvents';
import { DomainError } from '../errors/domainError';
import { ErrorCode } from '../errors/errorCodes';
// Tombstone import removed to prevent folders from being randomly deleted

export interface CreateFolderDTO {
  name: string;
  color: string;
  icon?: string;
  parentId?: ID;
  order?: number;
}

export interface UpdateFolderDTO extends Partial<CreateFolderDTO> {}

export class FolderService {
  constructor(
    private readonly folderRepository: IFolderRepository,
    private readonly promptRepository: IPromptRepository,
    private readonly hierarchyBuilder: IFolderHierarchyBuilder,
    private readonly specialFolderFactory: ISpecialFolderFactory,
    private readonly eventPublisher: DomainEventPublisher
  ) {}

  private async getMaxOrderForParent(parentId?: ID, excludeId?: ID): Promise<number> {
    const siblings = await this.folderRepository.findByParent(parentId ?? null);
    // Exclude a folder being moved so its own (stale) order can't inflate the max
    // when it already lives under the target parent.
    const folders = (siblings.data ?? []).filter(f => f.id !== excludeId);
    if (!folders.length) {
      return 0;
    }
    return Math.max(...folders.map(f => f.order));
  }

  async getFolder(id: ID): Promise<Folder | null> {
    return this.folderRepository.findById(id);
  }

  async getFolderChildren(id: ID): Promise<Folder[]> {
    const result = await this.folderRepository.findByParent(id);
    return result.data ?? [];
  }

  async getRootFolders(): Promise<Folder[]> {
    const result = await this.folderRepository.findByParent(null);
    return result.data ?? [];
  }

  async createFolder(dto: CreateFolderDTO, userId?: ID): Promise<Folder> {
    // Get max order for the parent folder
    const maxOrder = await this.getMaxOrderForParent(dto.parentId);

    // Validate parent folder if specified
    if (dto.parentId) {
      const parentFolder = await this.folderRepository.findById(dto.parentId);
      if (!parentFolder) {
        throw new DomainError(
          ErrorCode.FOLDER_NOT_FOUND,
          `Parent folder ${dto.parentId} not found`
        );
      }
      if (parentFolder.isSpecial()) {
        throw new DomainError(
          ErrorCode.SPECIAL_FOLDER_MODIFICATION,
          'Cannot create folders inside special folders'
        );
      }
    }

    // Validate unique name within parent
    const isNameUnique = await this.folderRepository.validateUniqueName(dto.name, dto.parentId);
    if (!isNameUnique) {
      throw new DomainError(
        ErrorCode.DUPLICATE_FOLDER_NAME,
        `Folder name "${dto.name}" already exists in this location`
      );
    }

    // Create folder
    const folder = Folder.create({
      ...dto,
      order: dto.order ?? maxOrder + 1,
      createdBy: userId,
      updatedBy: userId,
    });

    // Save to repository
    const savedFolder = await this.folderRepository.create(folder);

    // Publish event
    await this.eventPublisher.publish(DomainEventFactory.folderCreated(savedFolder, userId));

    return savedFolder;
  }

  async updateFolder(id: ID, changes: UpdateFolderDTO, userId?: ID): Promise<Folder> {
    // Get existing folder
    const existingFolder = await this.folderRepository.findById(id);
    if (!existingFolder) {
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${id} not found`);
    }

    // Prevent special folder modification
    if (existingFolder.isSpecial()) {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders cannot be modified'
      );
    }

    // A parentId change is a reparent and must be validated exactly like
    // moveFolder — otherwise updateFolder let a caller reparent a folder with
    // no circular-reference / special-parent / target-exists check, and checked
    // name uniqueness against the OLD parent (#112).
    const reparenting =
      changes.parentId !== undefined && changes.parentId !== existingFolder.parentId;
    if (reparenting) {
      await this.assertValidParent(id, changes.parentId);
    }
    const effectiveParentId =
      changes.parentId !== undefined ? changes.parentId : existingFolder.parentId;

    // Validate name uniqueness against the EFFECTIVE parent when the name or the
    // parent is changing (moving into a parent that already has a same-named
    // folder is also a collision).
    const nameChanging = changes.name !== undefined && changes.name !== existingFolder.name;
    if (nameChanging || reparenting) {
      const effectiveName = changes.name ?? existingFolder.name;
      const isNameUnique = await this.folderRepository.validateUniqueName(
        effectiveName,
        effectiveParentId,
        id
      );
      if (!isNameUnique) {
        throw new DomainError(
          ErrorCode.DUPLICATE_FOLDER_NAME,
          `Folder name "${effectiveName}" already exists in this location`
        );
      }
    }

    // If order is not provided, maintain current order
    const order = changes.order ?? existingFolder.order;

    // Update folder
    const updatedFolder = existingFolder.update({
      ...changes,
      order,
      updatedBy: userId,
    });

    // Save to repository
    const savedFolder = await this.folderRepository.update(id, updatedFolder);

    // Publish event
    await this.eventPublisher.publish(
      DomainEventFactory.folderUpdated(id, changes, existingFolder, userId)
    );

    return savedFolder;
  }
  async deleteFolder(id: ID, userId?: ID): Promise<void> {
    console.log('DELETION FLOW: Domain Step 1 - FolderService.deleteFolder called for folder:', id);

    const folder = await this.folderRepository.findById(id);
    if (!folder) {
      console.log('DELETION FLOW: Domain Step 1.1 - Folder not found, throwing error');
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${id} not found`);
    }
    console.log('DELETION FLOW: Domain Step 1.2 - Found folder to delete:', {
      id: folder.id,
      name: folder.name,
      parentId: folder.parentId,
      isSpecial: folder.isSpecial(),
    });

    if (folder.isSpecial()) {
      console.log('DELETION FLOW: Domain Step 1.3 - Cannot delete special folder, throwing error');
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders cannot be deleted'
      );
    }

    // Tombstone creation removed to prevent folders from being randomly deleted
    console.log(
      'DELETION FLOW: Domain Step 1.4 - Tombstone creation skipped to prevent random deletions'
    );

    // Check if folder has children BEFORE deleting any prompts.
    // Deletion is not transactional, so this guard must run first — otherwise a
    // non-empty parent folder would have its prompts irreversibly deleted and
    // then abort with an error, leaving the user with data loss and a failure.
    console.log('DELETION FLOW: Domain Step 1.5 - Checking if folder has children');
    const hasChildren = await this.folderRepository.hasChildren(id);
    console.log('DELETION FLOW: Domain Step 1.6 - Folder has children:', hasChildren);

    if (hasChildren) {
      console.log(
        'DELETION FLOW: Domain Step 1.7 - Cannot delete folder with children, throwing error'
      );
      throw new DomainError(
        ErrorCode.VALIDATION_ERROR,
        'Cannot delete folder that contains subfolders'
      );
    }

    // Check if folder has prompts and delete them
    console.log('DELETION FLOW: Domain Step 2 - Checking if folder has prompts');
    const promptCount = await this.promptRepository.countByFolder(id);
    console.log('DELETION FLOW: Domain Step 2.1 - Folder has', promptCount, 'prompts');

    if (promptCount > 0) {
      console.log('DELETION FLOW: Domain Step 2.2 - Deleting all prompts in folder');

      // Get all prompts in the folder
      const folderPrompts = await this.promptRepository.findByFolder(id);
      const promptsToDelete = folderPrompts.data || [];

      console.log(
        `DELETION FLOW: Domain Step 2.3 - Found ${promptsToDelete.length} prompts to delete`
      );

      // Delete each prompt and create tombstones
      for (const prompt of promptsToDelete) {
        console.log(`DELETION FLOW: Domain Step 2.4 - Deleting prompt ${prompt.id}`);

        // Tombstone creation removed to prevent folders from being randomly deleted
        console.log(
          'DELETION FLOW: Domain Step 2.5 - Prompt tombstone creation skipped to prevent random deletions'
        );

        // Delete the prompt from the repository
        await this.promptRepository.delete(prompt.id);
        console.log(`DELETION FLOW: Domain Step 2.6 - Deleted prompt ${prompt.id}`);

        // Publish event for the deleted prompt
        await this.eventPublisher.publish(DomainEventFactory.promptDeleted(prompt.id, id, userId));
        console.log(
          `DELETION FLOW: Domain Step 2.7 - Published event for deleted prompt ${prompt.id}`
        );
      }

      console.log('DELETION FLOW: Domain Step 2.8 - All prompts in folder deleted successfully');
    }

    console.log('DELETION FLOW: Domain Step 4 - Calling folderRepository.delete');
    await this.folderRepository.delete(id);
    console.log('DELETION FLOW: Domain Step 4.1 - Repository delete operation completed');

    console.log('DELETION FLOW: Domain Step 5 - Publishing folderDeleted event');
    const folderDeletedEvent = DomainEventFactory.folderDeleted(id, folder.parentId, userId);
    console.log('DELETION FLOW: Domain Step 5.1 - Created folderDeleted event:', {
      type: folderDeletedEvent.type,
      payload: folderDeletedEvent.payload,
      metadata: folderDeletedEvent.metadata,
    });

    await this.eventPublisher.publish(folderDeletedEvent);
    console.log('DELETION FLOW: Domain Step 5.2 - Event published successfully');

    console.log('DELETION FLOW: Domain Step 6 - FolderService.deleteFolder completed successfully');
  }

  async moveFolder(id: ID, targetParentId?: ID, userId?: ID): Promise<Folder> {
    const folder = await this.folderRepository.findById(id);
    if (!folder) {
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${id} not found`);
    }

    if (folder.isSpecial()) {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders cannot be moved'
      );
    }

    await this.assertValidParent(id, targetParentId);

    // Get max order for the target parent, excluding the folder being moved so
    // we don't get an off-by-one when it already lives under that parent.
    const maxOrder = await this.getMaxOrderForParent(targetParentId, id);

    // Apply parent + order in a single repository write. Previously this called
    // updateFolder() just to set the order, which re-validated name uniqueness,
    // fired a second (redundant) event, threw for special folders, and risked a
    // partial-failure inconsistency across two separate writes.
    const updatedFolder = await this.folderRepository.moveToParent(
      id,
      targetParentId,
      maxOrder + 1
    );

    await this.eventPublisher.publish(
      DomainEventFactory.folderMoved(id, folder.parentId, targetParentId, userId)
    );

    return updatedFolder;
  }

  async updateFolderOrder(id: ID, newOrder: number, userId?: ID): Promise<Folder> {
    const folder = await this.folderRepository.findById(id);
    if (!folder) {
      throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${id} not found`);
    }

    if (folder.isSpecial()) {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders order cannot be modified'
      );
    }

    const updatedFolder = await this.folderRepository.updateOrder(id, newOrder);

    await this.eventPublisher.publish(
      DomainEventFactory.folderUpdated(id, { order: newOrder }, folder, userId)
    );

    return updatedFolder;
  }

  async bulkUpdateFolderOrder(
    updates: Array<{ id: ID; order: number }>,
    userId?: ID
  ): Promise<void> {
    // Validate all folders exist and none are special
    for (const update of updates) {
      const folder = await this.folderRepository.findById(update.id);
      if (!folder) {
        throw new DomainError(ErrorCode.FOLDER_NOT_FOUND, `Folder ${update.id} not found`);
      }
      if (folder.isSpecial()) {
        throw new DomainError(
          ErrorCode.SPECIAL_FOLDER_MODIFICATION,
          'Special folders order cannot be modified'
        );
      }
    }

    await this.folderRepository.bulkUpdateOrder(updates);

    // Publish events
    for (const update of updates) {
      const folder = await this.folderRepository.findById(update.id);
      if (folder) {
        await this.eventPublisher.publish(
          DomainEventFactory.folderUpdated(update.id, { order: update.order }, folder, userId)
        );
      }
    }
  }

  /**
   * Validate that `targetParentId` is a legal parent for folder `id`:
   * it exists, is not a special folder, and the move introduces no circular
   * reference. A null/undefined target (root) is always valid. Shared by
   * moveFolder and updateFolder so both enforce the same rules (#112).
   */
  private async assertValidParent(id: ID, targetParentId?: ID): Promise<void> {
    if (!targetParentId) return;

    const targetParent = await this.folderRepository.findById(targetParentId);
    if (!targetParent) {
      throw new DomainError(
        ErrorCode.FOLDER_NOT_FOUND,
        `Target parent folder ${targetParentId} not found`
      );
    }

    if (targetParent.isSpecial()) {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Cannot move folders into special folders'
      );
    }

    const allFolders = await this.folderRepository.findAll();
    const hierarchy = this.hierarchyBuilder.buildHierarchy(allFolders);
    if (!this.hierarchyBuilder.validateMove(hierarchy, id, targetParentId)) {
      throw new DomainError(
        ErrorCode.VALIDATION_ERROR,
        'Moving folder would create circular reference'
      );
    }
  }

  async initializeSpecialFolders(userId?: ID): Promise<Folder[]> {
    const specialFolders = [
      this.specialFolderFactory.createAllPromptsFolder(),
      this.specialFolderFactory.createFavoritesFolder(),
      this.specialFolderFactory.createRecentlyUsedFolder(),
      this.specialFolderFactory.createArchivedFolder(),
    ];

    const savedFolders: Folder[] = [];

    for (const folder of specialFolders) {
      // Idempotent: special folders have fixed ids, so a second run would throw
      // on the duplicate id and abort initialization. Skip any that already
      // exist and return the stored copy instead (#112).
      const existing = await this.folderRepository.findById(folder.id);
      if (existing) {
        savedFolders.push(existing);
        continue;
      }

      const savedFolder = await this.folderRepository.create(folder);
      savedFolders.push(savedFolder);

      await this.eventPublisher.publish(DomainEventFactory.folderCreated(savedFolder, userId));
    }

    return savedFolders;
  }
}
